import { isAxiosError } from 'axios'

/**
 * Turn an Axios failure into the sentence to show the user.
 *
 * Lives here rather than in `api-utils.ts` so a presentational component can use
 * it: `api-utils` imports `@/api/connector`, which imports `@/main` and mounts
 * the app, so anything pulling it in drags the whole app boot into its module
 * graph (and breaks in jsdom, which has no `#root`).
 */
export function getApiErrorMessage(
  error: unknown,
  fallbackMessage = ''
): string {
  const defaultMsg = fallbackMessage
  if (isAxiosError(error)) {
    switch (error.response?.status) {
      case 400: {
        const data = error.response?.data
        if (
          data?.message &&
          Array.isArray(data.message) &&
          data.message.length > 0
        ) {
          return data.message.join('\n')
        }
        return 'Veuillez vérifier les informations saisies.'
      }
      case 401:
        return 'Vous devez être connecté pour effectuer cette action.'
      case 403:
        return "Vous n'avez pas les droits nécessaires pour effectuer cette action."
      case 404:
        return "La ressource demandée n'a pas été trouvée."
      case 409: {
        const data = error.response?.data
        if (data?.message && typeof data.message === 'string') {
          return data.message
        }
        return 'Un conflit est survenu lors de la sauvegarde.'
      }
      // 422 = the request is well-formed but the data it references is not. The
      // server's message is already French AND names the missing field (#1487's
      // billed-identity hard block) — swallowing it for a generic sentence is
      // exactly what the owner rejected: « ça devrait même être affiché
      // clairement que je sache quel est le problème ».
      case 422: {
        const data = error.response?.data
        if (typeof data?.message === 'string' && data.message) {
          return data.message
        }
        if (Array.isArray(data?.message) && data.message.length > 0) {
          return data.message.join('\n')
        }
        return "L'opération est impossible avec les données actuelles."
      }
      case 429:
        return 'Trop de requêtes. Veuillez réessayer plus tard.'
      case 500:
      case 502:
      case 503:
        return 'Le service est momentanément indisponible. Veuillez réessayer plus tard.'
      default:
        return defaultMsg
    }
  }
  return defaultMsg
}
